import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  Send,
  StickyNote,
  CheckCircle,
  Bot,
  X,
  ChevronDown,
  ChevronRight,
  User,
  Mail,
  Phone,
  Tag,
  Star,
  Ban,
  Plus,
  Loader2,
  Inbox,
  MessageSquare,
  XCircle,
  Pencil,
  Check,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatRelativeTime } from '@/utils/format'
import { conversationsService, ConversationMessage, Conversation } from '@/services/conversations.service'
import { contactService } from '@/services/contact.service'
import { TaskProgressPanel } from '@/components/conversations/TaskProgressPanel'
import { DocumentsPanel } from '@/components/conversations/DocumentsPanel'
import {
  useInboxConversations,
  useChannelAvailability,
  useSendInboxMessage,
  useCreateNote,
  useAssignConversation,
  useUpdateConversationStatus,
  useUpdateCustomerProfile,
  useUpdateCustomerTags,
} from '@/hooks/useInbox'
import { useTeamStore } from '@/store/teamStore'
import type { InboxConversation, ChannelAvailability } from '@/services/inbox.service'
import toast from 'react-hot-toast'

// ============= Helpers =============

type FilterTab = 'all' | 'mine' | 'unassigned'

function getChannelIcon(channel: string): string {
  switch (channel?.toLowerCase()) {
    case 'instagram': return '\uD83D\uDCF7'
    case 'facebook': return '\uD83D\uDC64'
    case 'whatsapp': return '\uD83D\uDCAC'
    case 'telegram': return '\u2708\uFE0F'
    case 'web': return '\uD83C\uDF10'
    case 'web_widget': return '\uD83C\uDF10'
    default: return '\uD83D\uDCAC'
  }
}

function getChannelLabel(channel: string): string {
  switch (channel?.toLowerCase()) {
    case 'instagram': return 'Instagram'
    case 'facebook': return 'Facebook'
    case 'whatsapp': return 'WhatsApp'
    case 'telegram': return 'Telegram'
    case 'web': return 'Web'
    case 'web_widget': return 'Web Widget'
    default: return channel || 'Unknown'
  }
}

function getStatusBadge(status: string) {
  const styles: Record<string, string> = {
    escalated: 'bg-red-100 text-red-700',
    waiting_agent: 'bg-amber-100 text-amber-700',
    active: 'bg-green-100 text-green-700',
    resolved: 'bg-blue-100 text-blue-700',
    closed: 'bg-gray-100 text-gray-700',
  }
  return styles[status] || 'bg-gray-100 text-gray-700'
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    escalated: 'Escalated',
    waiting_agent: 'Waiting',
    active: 'Active',
    resolved: 'Resolved',
    closed: 'Closed',
  }
  return labels[status] || status
}

function truncate(text: string, maxLength: number): string {
  if (!text) return ''
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
}

function getCustomerDisplayName(customer: InboxConversation['customer'] | undefined, channelType?: string): string {
  if (customer?.name
    && !/^[0-9a-f-]{20,}$/i.test(customer.name)
    && !/^test[-_]chat/i.test(customer.name)
    && !/^custom\s/i.test(customer.name)
    && !/^facebook\s\d/i.test(customer.name)
    && customer.name.length < 50
  ) {
    return customer.name
  }
  if (customer?.phone) return customer.phone
  if (customer?.email) return customer.email
  const channelFallback: Record<string, string> = { facebook: 'Facebook User', telegram: 'Telegram User', instagram: 'Instagram User', whatsapp: 'WhatsApp User' }
  const ch = channelType || customer?.channel_type || ''
  return channelFallback[ch] || 'Customer'
}

// ============= Left Panel: Conversation List =============

function ConversationListItem({
  conversation,
  isSelected,
  onClick,
}: {
  conversation: InboxConversation
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-3 border-b border-gray-100 transition-colors',
        'hover:bg-gray-50 focus:outline-none focus:bg-gray-50',
        isSelected && 'bg-blue-50 border-l-2 border-l-blue-500'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="text-base mt-0.5 flex-shrink-0" title={conversation.channel_type}>
          {getChannelIcon(conversation.channel_type)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              {getCustomerDisplayName(conversation.customer, conversation.channel_type)}
            </span>
            {(conversation.metadata as any)?.customer_metadata?.lead_score && (
              <span className={cn(
                'ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-md text-[9px] font-black text-white flex-shrink-0',
                (conversation.metadata as any).customer_metadata.lead_score >= 9 ? 'bg-emerald-500' :
                (conversation.metadata as any).customer_metadata.lead_score >= 7 ? 'bg-blue-500' :
                (conversation.metadata as any).customer_metadata.lead_score >= 5 ? 'bg-amber-400' :
                (conversation.metadata as any).customer_metadata.lead_score >= 3 ? 'bg-orange-400' : 'bg-gray-400'
              )}>
                {(conversation.metadata as any).customer_metadata.lead_score}
              </span>
            )}
            <span className="text-xs text-gray-400 flex-shrink-0">
              {conversation.last_customer_message_at
                ? formatRelativeTime(conversation.last_customer_message_at)
                : ''}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {truncate(conversation.last_message_preview || '', 60)}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={cn(
                'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                getStatusBadge(conversation.status)
              )}
            >
              {getStatusLabel(conversation.status)}
            </span>
            {conversation.assigned_to && (
              <span className="text-[10px] text-gray-400 truncate">
                <User className="inline w-3 h-3 mr-0.5" />
                {conversation.assigned_to.user_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

function LeftPanel({
  selectedId,
  onSelect,
}: {
  selectedId: string | null
  onSelect: (conversation: InboxConversation) => void
}) {
  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [search, setSearch] = useState('')
  const [scoreFilter, setScoreFilter] = useState<string>('all')

  const { data, isLoading, error } = useInboxConversations({
    filter: filterTab === 'all' ? undefined : filterTab,
    limit: 50,
  })

  const conversations = data?.conversations || []

  const filtered = useMemo(() => {
    let result = conversations
    if (search.trim()) {
      const term = search.toLowerCase()
      result = result.filter(
        (c) =>
          c.customer?.name?.toLowerCase().includes(term) ||
          c.last_message_preview?.toLowerCase().includes(term) ||
          c.channel_type?.toLowerCase().includes(term)
      )
    }
    if (scoreFilter !== 'all') {
      result = result.filter(c => {
        const score = (c.metadata as any)?.customer_metadata?.lead_score;
        if (scoreFilter === 'unscored') return !score;
        if (scoreFilter === 'hot') return score >= 9;
        if (scoreFilter === 'good') return score >= 7 && score <= 8;
        if (scoreFilter === 'medium') return score >= 5 && score <= 6;
        if (scoreFilter === 'low') return score >= 3 && score <= 4;
        if (scoreFilter === 'minimal') return score >= 1 && score <= 2;
        return true;
      })
    }
    return result
  }, [conversations, search, scoreFilter])

  const tabs: { label: string; value: FilterTab }[] = [
    { label: 'All', value: 'all' },
    { label: 'Mine', value: 'mine' },
    { label: 'Unassigned', value: 'unassigned' },
  ]

  return (
    <div className="w-80 border-r border-gray-200 flex flex-col bg-white h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Inbox</h2>
        {/* Tabs */}
        <div className="flex gap-1 mb-2">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterTab(tab.value)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                filterTab === tab.value
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            >
              <X className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
        {/* Score filter */}
        <select
          value={scoreFilter}
          onChange={e => setScoreFilter(e.target.value)}
          className="w-full mt-1.5 px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
        >
          <option value="all">All Scores</option>
          <option value="hot">{'\uD83D\uDFE2'} Hot (9-10)</option>
          <option value="good">{'\uD83D\uDD35'} Good (7-8)</option>
          <option value="medium">{'\uD83D\uDFE1'} Medium (5-6)</option>
          <option value="low">{'\uD83D\uDFE0'} Low (3-4)</option>
          <option value="minimal">{'\u26AA'} Minimal (1-2)</option>
          <option value="unscored">&mdash; Unscored</option>
        </select>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-xs text-red-500">
            Failed to load conversations
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-400">
            <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            No conversations found
          </div>
        ) : (
          filtered.map((c) => (
            <ConversationListItem
              key={c.id}
              conversation={c}
              isSelected={selectedId === c.id}
              onClick={() => onSelect(c)}
            />
          ))
        )}
      </div>

      {/* Footer count */}
      {!isLoading && (
        <div className="px-3 py-2 border-t border-gray-100 text-[10px] text-gray-400">
          {filtered.length} conversation{filtered.length !== 1 ? 's' : ''}
          {data?.total != null && data.total > filtered.length && ` of ${data.total}`}
        </div>
      )}
    </div>
  )
}

// ============= Middle Panel: Message Thread =============

function MessageBubble({ message }: { message: ConversationMessage }) {
  const senderType = message.sender_type || 'system'
  const channelType = (message.metadata?.channel_type as string) || ''
  const deliveryStatus = (message.metadata?.delivery_status as string) || ''
  const deliveryError = (message.metadata?.delivery_error as string) || ''
  const [showError, setShowError] = useState(false)

  if (senderType === 'system') {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
          {message.content}
        </span>
      </div>
    )
  }

  const isCustomer = senderType === 'customer'
  const isInternal = senderType === 'internal'
  const isFailed = deliveryStatus === 'failed'

  const alignment = isCustomer ? 'items-start' : 'items-end'
  const bubbleBg = isCustomer
    ? 'bg-blue-50'
    : isInternal
    ? 'bg-amber-50 border border-amber-200'
    : isFailed
    ? 'bg-red-50 border border-red-200'
    : senderType === 'human'
    ? 'bg-green-50'
    : 'bg-gray-100'

  const badge =
    senderType === 'agent'
      ? { label: 'AI', className: 'bg-gray-200 text-gray-600' }
      : senderType === 'internal'
      ? { label: 'Note', className: 'bg-amber-200 text-amber-700' }
      : null

  return (
    <div className={cn('flex flex-col mb-3', alignment)}>
      <div className={cn('max-w-[75%] rounded-lg px-3 py-2', bubbleBg)}>
        {/* Header line */}
        <div className="flex items-center gap-1.5 mb-1">
          {channelType && (
            <span className="text-[10px]" title={channelType}>
              {getChannelIcon(channelType)}
            </span>
          )}
          {badge && (
            <span
              className={cn(
                'text-[10px] font-medium px-1.5 py-0.5 rounded',
                badge.className
              )}
            >
              {badge.label}
            </span>
          )}
          {senderType === 'human' && message.sender_name && (
            <span className="text-[10px] font-medium text-green-700">
              {message.sender_name}
            </span>
          )}
          {senderType === 'internal' && message.sender_name && (
            <span className="text-[10px] font-medium text-amber-700">
              {message.sender_name}
            </span>
          )}
          {isCustomer && (
            <span className="text-[10px] font-medium text-blue-700">Customer</span>
          )}
        </div>
        {/* Content */}
        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {message.content}
        </p>
        {/* Timestamp + delivery status */}
        <div className={cn('mt-1 flex items-center gap-1.5', isCustomer ? 'text-blue-400' : 'text-gray-400')}>
          <span className="text-[10px]">{formatRelativeTime(message.created_at)}</span>
          {isFailed && (
            <button
              onClick={() => setShowError(!showError)}
              className="flex items-center gap-0.5 text-[10px] font-medium text-red-500 hover:text-red-700 transition-colors"
              title="Click to see error details"
            >
              <AlertTriangle className="w-3 h-3" />
              Not delivered
            </button>
          )}
          {deliveryStatus === 'delivered' && senderType === 'human' && (
            <span className="text-[10px] text-green-500">
              <Check className="w-3 h-3 inline" />
            </span>
          )}
        </div>
      </div>
      {/* Error details popover */}
      {isFailed && showError && deliveryError && (
        <div className={cn(
          'max-w-[75%] mt-1 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600',
          !isCustomer && 'self-end'
        )}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-medium">Delivery Error</span>
            <button onClick={() => setShowError(false)} className="text-red-400 hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          </div>
          <p className="text-red-500">{deliveryError}</p>
        </div>
      )}
    </div>
  )
}

function MiddlePanel({
  conversation,
  onStatusChange,
}: {
  conversation: InboxConversation | null
  onStatusChange: (status: string) => void
}) {
  const [messageText, setMessageText] = useState('')
  const [isNoteMode, setIsNoteMode] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null)
  const [showChannelDropdown, setShowChannelDropdown] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const channelDropdownRef = useRef<HTMLDivElement>(null)

  const sendMessage = useSendInboxMessage()
  const createNote = useCreateNote()
  const updateStatus = useUpdateConversationStatus()

  // Fetch messages
  const {
    data: messages = [],
    isLoading: messagesLoading,
  } = useQuery({
    queryKey: ['conversation-messages', conversation?.id],
    queryFn: () => conversationsService.getMessages(conversation!.id),
    enabled: !!conversation?.id,
    refetchInterval: 5000,
  })

  // Fetch channel availability
  const { data: channelData } = useChannelAvailability(conversation?.customer?.id)
  const channels: ChannelAvailability[] = channelData?.channels || []

  // Set default channel when conversation changes
  useEffect(() => {
    if (conversation?.channel_type) {
      setSelectedChannel(conversation.channel_type)
    }
  }, [conversation?.id, conversation?.channel_type])

  // Auto-scroll to bottom only when new messages arrive
  const prevMessageCountRef = useRef(0)
  useEffect(() => {
    const count = messages?.length || 0
    if (count > prevMessageCountRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCountRef.current = count
  }, [messages])

  // Reset input when conversation changes
  useEffect(() => {
    setMessageText('')
    setIsNoteMode(false)
  }, [conversation?.id])

  // Close channel dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target as Node)) {
        setShowChannelDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSend = useCallback(async () => {
    if (!messageText.trim() || !conversation) return

    const text = messageText.trim()
    setMessageText('')

    try {
      if (isNoteMode) {
        await createNote.mutateAsync({
          conversationId: conversation.id,
          content: text,
        })
        toast.success('Note added')
      } else {
        const channelId = channels.find(
          (ch) => ch.channel_type === selectedChannel
        )?.channel_id
        const result = await sendMessage.mutateAsync({
          conversationId: conversation.id,
          content: text,
          channelId,
        })
        if (result?.metadata?.delivery_status === 'failed') {
          toast.error('Message saved but delivery failed — check message for details')
        } else {
          toast.success('Message sent')
        }
      }
    } catch {
      toast.error(isNoteMode ? 'Failed to add note' : 'Failed to send message')
      setMessageText(text) // Restore on failure
    }
  }, [messageText, conversation, isNoteMode, selectedChannel, channels, createNote, sendMessage])

  const handleStatusAction = useCallback(
    async (newStatus: string) => {
      if (!conversation) return
      try {
        await updateStatus.mutateAsync({
          conversationId: conversation.id,
          status: newStatus,
        })
        onStatusChange(newStatus)
        toast.success(`Conversation ${newStatus === 'resolved' ? 'resolved' : newStatus === 'active' ? 'returned to AI' : 'closed'}`)
      } catch {
        toast.error('Failed to update status')
      }
    },
    [conversation, updateStatus, onStatusChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const isSending = sendMessage.isPending || createNote.isPending
  const canReturnToAI =
    conversation?.status === 'escalated' || conversation?.status === 'waiting_agent'

  // Empty state
  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-400">Select a conversation to get started</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-white h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <span className="text-base">{getChannelIcon(conversation.channel_type)}</span>
          <span className="text-sm font-medium text-gray-900">
            {getCustomerDisplayName(conversation.customer, conversation.channel_type)}
          </span>
          <span
            className={cn(
              'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
              getStatusBadge(conversation.status)
            )}
          >
            {getStatusLabel(conversation.status)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleStatusAction('resolved')}
            disabled={updateStatus.isPending}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Resolve
          </button>
          {canReturnToAI && (
            <button
              onClick={() => handleStatusAction('active')}
              disabled={updateStatus.isPending}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50"
            >
              <Bot className="w-3.5 h-3.5" />
              Return to AI
            </button>
          )}
          <button
            onClick={() => handleStatusAction('closed')}
            disabled={updateStatus.isPending}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            <XCircle className="w-3.5 h-3.5" />
            Close
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-4">
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-gray-400">No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply area */}
      <div className="border-t border-gray-200 bg-white p-3">
        {/* Channel selector + note toggle */}
        <div className="flex items-center justify-between mb-2">
          <div className="relative" ref={channelDropdownRef}>
            <button
              onClick={() => setShowChannelDropdown(!showChannelDropdown)}
              className="flex items-center gap-1.5 px-2 py-1 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <span>{selectedChannel ? getChannelIcon(selectedChannel) : '\uD83D\uDCAC'}</span>
              <span className="text-gray-600">
                {selectedChannel ? getChannelLabel(selectedChannel) : 'Select channel'}
              </span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </button>
            {showChannelDropdown && (
              <div className="absolute bottom-full left-0 mb-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                {channels.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400">No channels available</div>
                ) : (
                  channels.map((ch) => (
                    <button
                      key={ch.channel_id || ch.channel_type}
                      onClick={() => {
                        if (ch.available) {
                          setSelectedChannel(ch.channel_type)
                          setShowChannelDropdown(false)
                        }
                      }}
                      disabled={!ch.available}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors',
                        ch.available
                          ? 'hover:bg-gray-50 text-gray-700'
                          : 'text-gray-300 cursor-not-allowed'
                      )}
                      title={!ch.available ? ch.reason || 'Channel unavailable' : undefined}
                    >
                      <span>{getChannelIcon(ch.channel_type)}</span>
                      <span>{getChannelLabel(ch.channel_type)}</span>
                      {!ch.available && (
                        <span className="ml-auto text-[10px] text-gray-300">unavailable</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => setIsNoteMode(!isNoteMode)}
            className={cn(
              'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors',
              isNoteMode
                ? 'bg-amber-100 text-amber-700'
                : 'text-gray-500 hover:bg-gray-100'
            )}
          >
            <StickyNote className="w-3.5 h-3.5" />
            {isNoteMode ? 'Note Mode' : 'Note'}
          </button>
        </div>

        {/* Input */}
        <div
          className={cn(
            'flex items-end gap-2 border rounded-lg transition-colors',
            isNoteMode
              ? 'border-amber-300 bg-amber-50/30'
              : 'border-gray-200 bg-white'
          )}
        >
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isNoteMode ? 'Write an internal note...' : 'Type your reply...'}
            rows={2}
            className={cn(
              'flex-1 resize-none px-3 py-2 text-sm focus:outline-none rounded-lg bg-transparent',
              isNoteMode ? 'placeholder:text-amber-400' : 'placeholder:text-gray-400'
            )}
          />
          <button
            onClick={handleSend}
            disabled={!messageText.trim() || isSending}
            className={cn(
              'flex items-center gap-1 px-3 py-2 mr-1 mb-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50',
              isNoteMode
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {isSending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : isNoteMode ? (
              <StickyNote className="w-3.5 h-3.5" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            {isNoteMode ? 'Add Note' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============= Right Panel: Customer Details =============

function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 transition-colors"
      >
        {title}
        {open ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  )
}

function EditableField({
  value,
  icon: Icon,
  onSave,
}: {
  value: string
  icon: React.ElementType
  onSave: (value: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [editing])

  const handleSave = () => {
    if (draft !== value) {
      onSave(draft)
    }
    setEditing(false)
  }

  return (
    <div className="flex items-center gap-2 py-1.5">
      <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      {editing ? (
        <div className="flex items-center gap-1 flex-1">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') { setDraft(value); setEditing(false) }
            }}
            className="flex-1 text-sm border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button onClick={handleSave} className="text-green-600 hover:text-green-700">
            <Check className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setDraft(value); setEditing(false) }} className="text-gray-400 hover:text-gray-500">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <span className="text-sm text-gray-700 truncate">{value || <span className="text-gray-300 italic">Not set</span>}</span>
          <button onClick={() => setEditing(true)} className="text-gray-300 hover:text-gray-500 flex-shrink-0">
            <Pencil className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  )
}

function TagToggle({
  label,
  icon: Icon,
  active,
  activeColor,
  onToggle,
}: {
  label: string
  icon: React.ElementType
  active: boolean
  activeColor: string
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors',
        active
          ? activeColor
          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  )
}

function CollectedDataSection({
  conversation,
}: {
  conversation: InboxConversation
}) {
  const contactId = conversation.customer?.contact_id

  // Fetch contact detail if contact_id exists (gives unified collected_data + channels)
  const { data: contactDetail } = useQuery({
    queryKey: ['contact-detail', contactId],
    queryFn: () => contactService.getById(contactId!),
    enabled: !!contactId,
  })

  // Fetch current conversation's collected_data
  const { data: fullConversation } = useQuery({
    queryKey: ['conversation-detail', conversation.id],
    queryFn: () => conversationsService.getConversation(conversation.id),
    enabled: !!conversation.id,
  })

  const unifiedContext = contactDetail?.unified_context
  const linkedChannels = unifiedContext?.channels || []
  const unifiedCollectedData = unifiedContext?.collected_data || {}
  const conversationCollectedData = fullConversation?.collected_data || {}

  // Merge: unified (cross-channel) + current conversation's data
  const mergedData: Record<string, unknown> = {
    ...unifiedCollectedData,
    ...conversationCollectedData,
  }

  const entries = Object.entries(mergedData).filter(
    ([, v]) => v != null && v !== ''
  )

  if (entries.length === 0 && linkedChannels.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic py-2">No collected data yet</div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Linked channels badge row */}
      {linkedChannels.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Linked:</span>
          {linkedChannels.map((ch) => (
            <span
              key={ch}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full"
            >
              {getChannelIcon(ch)} {getChannelLabel(ch)}
            </span>
          ))}
        </div>
      )}
      {/* Key-value rows */}
      <div className="space-y-0.5">
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-start justify-between gap-2 py-1">
            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
              {key.replace(/_/g, ' ')}
            </span>
            <span className="text-xs text-gray-700 text-right break-all max-w-[60%]">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function TasksSection({
  conversation,
}: {
  conversation: InboxConversation
}) {
  const contactId = conversation.customer?.contact_id

  // Fetch linked customers' conversations if contact exists
  const { data: contactDetail } = useQuery({
    queryKey: ['contact-detail', contactId],
    queryFn: () => contactService.getById(contactId!),
    enabled: !!contactId,
  })

  const linkedCustomers = contactDetail?.linked_customers || []

  // Fetch conversations for each linked customer to find all conversation IDs
  const { data: linkedConversations } = useQuery({
    queryKey: ['linked-conversations', contactId, linkedCustomers.map((c) => c.id).join(',')],
    queryFn: async () => {
      const allConvs: Conversation[] = []
      for (const customer of linkedCustomers) {
        try {
          const result = await conversationsService.getConversations({
            search: customer.external_id,
            limit: 20,
          })
          // Filter to only this customer's conversations, excluding the current one
          const customerConvs = result.data.filter(
            (c) => c.customer_id === customer.id && c.id !== conversation.id
          )
          allConvs.push(...customerConvs)
        } catch {
          // skip failures for individual customers
        }
      }
      return allConvs
    },
    enabled: !!contactId && linkedCustomers.length > 0,
  })

  const otherConversations = linkedConversations || []

  return (
    <div className="space-y-3">
      {/* Current conversation tasks */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
            Current
          </span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full">
            {getChannelIcon(conversation.channel_type)} {getChannelLabel(conversation.channel_type)}
          </span>
        </div>
        <TaskProgressPanel conversationId={conversation.id} />
      </div>

      {/* Other linked conversations' tasks */}
      {otherConversations.map((conv) => (
        <div key={conv.id}>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full">
              {getChannelIcon(conv.channel_type)} {getChannelLabel(conv.channel_type)}
            </span>
            <span className="text-[10px] text-gray-400">
              {formatRelativeTime(conv.last_message_at)}
            </span>
          </div>
          <TaskProgressPanel conversationId={conv.id} />
        </div>
      ))}
    </div>
  )
}

function RightPanel({
  conversation,
}: {
  conversation: InboxConversation | null
}) {
  const { members, fetchMembers } = useTeamStore()
  const updateProfile = useUpdateCustomerProfile()
  const updateTags = useUpdateCustomerTags()
  const assignConversation = useAssignConversation()

  const [newTag, setNewTag] = useState('')
  const [customTags, setCustomTags] = useState<string[]>([])
  const [isHighPotential, setIsHighPotential] = useState(false)
  const [isBlocked, setIsBlocked] = useState(false)

  // Load team members
  useEffect(() => {
    fetchMembers().catch(() => {})
  }, [fetchMembers])

  // Sync local tag state from conversation metadata
  useEffect(() => {
    if (conversation) {
      const meta = conversation.metadata || {}
      setCustomTags((meta.custom_tags as string[]) || [])
      setIsHighPotential(!!(meta.is_high_potential))
      setIsBlocked(!!(meta.is_blocked))
    }
  }, [conversation?.id, conversation?.metadata])

  const handleProfileUpdate = useCallback(
    (field: string, value: string) => {
      if (!conversation?.customer?.id) return
      updateProfile.mutate(
        { customerId: conversation.customer.id, data: { [field]: value } },
        {
          onSuccess: () => toast.success('Profile updated'),
          onError: () => toast.error('Failed to update profile'),
        }
      )
    },
    [conversation?.customer?.id, updateProfile]
  )

  const handleTagToggle = useCallback(
    (field: 'is_high_potential' | 'is_blocked', current: boolean) => {
      if (!conversation?.customer?.id) return
      const newValue = !current
      if (field === 'is_high_potential') setIsHighPotential(newValue)
      if (field === 'is_blocked') setIsBlocked(newValue)
      updateTags.mutate(
        { customerId: conversation.customer.id, tags: { [field]: newValue } },
        {
          onError: () => {
            if (field === 'is_high_potential') setIsHighPotential(current)
            if (field === 'is_blocked') setIsBlocked(current)
            toast.error('Failed to update tag')
          },
        }
      )
    },
    [conversation?.customer?.id, updateTags]
  )

  const handleAddCustomTag = useCallback(() => {
    const tag = newTag.trim()
    if (!tag || !conversation?.customer?.id || customTags.includes(tag)) return
    const updated = [...customTags, tag]
    setCustomTags(updated)
    setNewTag('')
    updateTags.mutate(
      { customerId: conversation.customer.id, tags: { custom_tags: updated } },
      {
        onError: () => {
          setCustomTags(customTags)
          toast.error('Failed to add tag')
        },
      }
    )
  }, [newTag, conversation?.customer?.id, customTags, updateTags])

  const handleRemoveCustomTag = useCallback(
    (tag: string) => {
      if (!conversation?.customer?.id) return
      const updated = customTags.filter((t) => t !== tag)
      setCustomTags(updated)
      updateTags.mutate(
        { customerId: conversation.customer.id, tags: { custom_tags: updated } },
        {
          onError: () => {
            setCustomTags(customTags)
            toast.error('Failed to remove tag')
          },
        }
      )
    },
    [conversation?.customer?.id, customTags, updateTags]
  )

  const handleAssign = useCallback(
    (userId: string | null) => {
      if (!conversation) return
      assignConversation.mutate(
        { conversationId: conversation.id, userId },
        {
          onSuccess: () =>
            toast.success(userId ? 'Conversation assigned' : 'Conversation unassigned'),
          onError: () => toast.error('Failed to assign conversation'),
        }
      )
    },
    [conversation, assignConversation]
  )

  // Empty state
  if (!conversation) {
    return (
      <div className="w-96 border-l border-gray-200 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-xs text-gray-400">Select a conversation to view details</p>
        </div>
      </div>
    )
  }

  const customer = conversation.customer

  return (
    <div className="w-96 border-l border-gray-200 bg-white flex flex-col h-full overflow-y-auto">
      {/* Customer header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {getCustomerDisplayName(customer, conversation.channel_type)}
              </h3>
              {(conversation.metadata as any)?.customer_metadata?.lead_score && (
                <span className={cn(
                  'inline-flex items-center justify-center w-6 h-6 rounded-md text-[10px] font-black text-white flex-shrink-0',
                  (conversation.metadata as any).customer_metadata.lead_score >= 9 ? 'bg-emerald-500' :
                  (conversation.metadata as any).customer_metadata.lead_score >= 7 ? 'bg-blue-500' :
                  (conversation.metadata as any).customer_metadata.lead_score >= 5 ? 'bg-amber-400' :
                  (conversation.metadata as any).customer_metadata.lead_score >= 3 ? 'bg-orange-400' : 'bg-gray-400'
                )}>
                  {(conversation.metadata as any).customer_metadata.lead_score}
                </span>
              )}
            </div>
            {(conversation.metadata as any)?.customer_metadata?.lead_score_reason && (
              <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                {(conversation.metadata as any).customer_metadata.lead_score_reason}
              </p>
            )}
            <p className="text-xs text-gray-400">
              {getChannelIcon(conversation.channel_type)}{' '}
              {getChannelLabel(conversation.channel_type)}
              {customer?.external_id && (
                <span className="ml-1 text-gray-300">#{customer.external_id.slice(0, 8)}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Customer identity */}
      <CollapsibleSection title="Contact Info">
        <EditableField
          value={customer?.name || ''}
          icon={User}
          onSave={(v) => handleProfileUpdate('name', v)}
        />
        <EditableField
          value={customer?.email || ''}
          icon={Mail}
          onSave={(v) => handleProfileUpdate('email', v)}
        />
        <EditableField
          value={customer?.phone || ''}
          icon={Phone}
          onSave={(v) => handleProfileUpdate('phone', v)}
        />
      </CollapsibleSection>

      {/* Tags */}
      <CollapsibleSection title="Tags">
        <div className="flex flex-wrap gap-2 mb-2">
          <TagToggle
            label="High Potential"
            icon={Star}
            active={isHighPotential}
            activeColor="border-amber-300 bg-amber-50 text-amber-700"
            onToggle={() => handleTagToggle('is_high_potential', isHighPotential)}
          />
          <TagToggle
            label="Blocked"
            icon={Ban}
            active={isBlocked}
            activeColor="border-red-300 bg-red-50 text-red-700"
            onToggle={() => handleTagToggle('is_blocked', isBlocked)}
          />
        </div>
        {/* Custom tags */}
        <div className="flex flex-wrap gap-1 mb-2">
          {customTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full"
            >
              <Tag className="w-2.5 h-2.5" />
              {tag}
              <button
                onClick={() => handleRemoveCustomTag(tag)}
                className="ml-0.5 text-gray-400 hover:text-gray-600"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddCustomTag()
            }}
            placeholder="Add tag..."
            className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleAddCustomTag}
            disabled={!newTag.trim()}
            className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </CollapsibleSection>

      {/* Collected Data */}
      <CollapsibleSection title="Collected Data" defaultOpen={false}>
        <CollectedDataSection conversation={conversation} />
      </CollapsibleSection>

      {/* Tasks */}
      <CollapsibleSection title="Tasks" defaultOpen={false}>
        <TasksSection conversation={conversation} />
      </CollapsibleSection>

      {/* Documents */}
      <CollapsibleSection title="Documents" defaultOpen={false}>
        <DocumentsPanel conversationId={conversation.id} />
      </CollapsibleSection>

      {/* Assignment */}
      <CollapsibleSection title="Assignment">
        <select
          value={conversation.assigned_to?.user_id || ''}
          onChange={(e) => handleAssign(e.target.value || null)}
          className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
        >
          <option value="">Unassigned</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.user_name} ({m.role})
            </option>
          ))}
        </select>
        {assignConversation.isPending && (
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" />
            Updating...
          </div>
        )}
      </CollapsibleSection>

      {/* Conversation metadata */}
      <CollapsibleSection title="Conversation Info" defaultOpen={false}>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">View</span>
            <Link
              to={`/conversations/${conversation.id}`}
              className="text-blue-600 hover:text-blue-800 text-[10px] font-medium hover:underline"
            >
              Open in Conversations
            </Link>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">Channel</span>
            <span className="text-gray-600">
              {getChannelIcon(conversation.channel_type)} {getChannelLabel(conversation.channel_type)}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">Status</span>
            <span
              className={cn(
                'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                getStatusBadge(conversation.status)
              )}
            >
              {getStatusLabel(conversation.status)}
            </span>
          </div>
          {conversation.priority && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Priority</span>
              <span className="text-gray-600 capitalize">{conversation.priority}</span>
            </div>
          )}
          {conversation.last_human_reply && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Last reply by</span>
              <span className="text-gray-600">
                {conversation.last_human_reply.sender_name}{' '}
                <span className="text-gray-400">
                  {formatRelativeTime(conversation.last_human_reply.created_at)}
                </span>
              </span>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  )
}

// ============= Main InboxPage Component =============

function InboxPage() {
  const { id: urlConversationId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [selectedConversation, setSelectedConversation] = useState<InboxConversation | null>(null)

  // Load conversation from URL param
  const { data: inboxData } = useInboxConversations({ limit: 50 })
  const conversations = inboxData?.conversations || []

  useEffect(() => {
    if (urlConversationId && conversations.length > 0 && selectedConversation?.id !== urlConversationId) {
      const found = conversations.find(c => c.id === urlConversationId)
      if (found) setSelectedConversation(found)
    }
  }, [urlConversationId, conversations, selectedConversation?.id])

  const handleSelect = useCallback((conversation: InboxConversation) => {
    setSelectedConversation(conversation)
    navigate(`/inbox/${conversation.id}`, { replace: true })
  }, [navigate])

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      if (selectedConversation) {
        setSelectedConversation({ ...selectedConversation, status: newStatus })
      }
    },
    [selectedConversation]
  )

  return (
    <div className="-my-6 -mx-4 sm:-mx-6 lg:-mx-8 flex h-[calc(100vh-64px)] overflow-hidden">
      <LeftPanel
        selectedId={selectedConversation?.id || null}
        onSelect={handleSelect}
      />
      <MiddlePanel
        conversation={selectedConversation}
        onStatusChange={handleStatusChange}
      />
      <RightPanel conversation={selectedConversation} />
    </div>
  )
}

export { InboxPage }
